/**
 * useBrowserRecording Hook
 *
 * React hook for browser-based audio recording with chunked uploads.
 * Uses MediaRecorder API for audio capture, Web Audio API for level metering,
 * and integrates with useWakeLock for sleep prevention.
 *
 * Features:
 * - Real-time audio level monitoring (decibel meter)
 * - Chunked recording with configurable duration
 * - Wake lock integration for long recordings
 * - Browser compatibility detection
 */

import { useState, useCallback, useRef, useEffect } from "react";
import { useWakeLock } from "./useWakeLock";
import type {
  BrowserRecordingState,
  BrowserRecordingOptions,
  AudioLevelData,
  BrowserCompatibility,
} from "@/types/browser-recording";

/**
 * Default recording options
 */
const DEFAULT_OPTIONS: Required<
  Omit<
    BrowserRecordingOptions,
    | "onChunkReady"
    | "onRecordingStart"
    | "onRecordingStop"
    | "onError"
    | "onAudioLevelUpdate"
  >
> = {
  chunkDurationMs: 30000, // 30 seconds
  audioBitrate: 128000, // 128 kbps
  sampleRate: 48000, // 48 kHz
  echoCancellation: true,
  noiseSuppression: true,
};

/**
 * Initial recording state
 */
const INITIAL_STATE: BrowserRecordingState = {
  status: "idle",
  duration: 0,
  chunksRecorded: 0,
  chunksUploaded: 0,
  audioLevel: {
    decibels: -100,
    normalized: 0,
    peak: 0,
    level: "quiet",
  },
  error: null,
  sessionId: null,
  wakeLockActive: false,
  mimeType: null,
};

/**
 * MIME types to try in order of preference
 */
const MIME_TYPE_PREFERENCES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
  "audio/ogg;codecs=opus",
];

/**
 * Get the best supported MIME type for recording
 */
function getBestMimeType(): string | null {
  if (typeof MediaRecorder === "undefined") {
    return null;
  }

  for (const mimeType of MIME_TYPE_PREFERENCES) {
    if (MediaRecorder.isTypeSupported(mimeType)) {
      return mimeType;
    }
  }

  return null;
}

/**
 * Get all supported MIME types
 */
function getSupportedMimeTypes(): string[] {
  if (typeof MediaRecorder === "undefined") {
    return [];
  }

  return MIME_TYPE_PREFERENCES.filter((type) =>
    MediaRecorder.isTypeSupported(type)
  );
}

/**
 * Check browser compatibility for recording features
 */
export function checkBrowserCompatibility(): BrowserCompatibility {
  const mediaRecorder = typeof MediaRecorder !== "undefined";
  const webAudio =
    typeof AudioContext !== "undefined" ||
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    typeof (window as any).webkitAudioContext !== "undefined";
  const wakeLock = "wakeLock" in navigator;

  return {
    mediaRecorder,
    webAudio,
    wakeLock,
    recommendedMimeType: getBestMimeType(),
    supportedMimeTypes: getSupportedMimeTypes(),
  };
}

/**
 * Calculate audio level classification based on normalized value
 */
function classifyAudioLevel(
  normalized: number
): "quiet" | "normal" | "loud" | "clipping" {
  if (normalized < 0.1) return "quiet";
  if (normalized < 0.5) return "normal";
  if (normalized < 0.9) return "loud";
  return "clipping";
}

/**
 * Generate a unique session ID
 */
function generateSessionId(): string {
  return `rec_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
}

/**
 * Hook for browser-based audio recording
 *
 * @param options Recording options
 * @returns Recording state and control functions
 */
export function useBrowserRecording(options: BrowserRecordingOptions = {}) {
  const {
    chunkDurationMs = DEFAULT_OPTIONS.chunkDurationMs,
    audioBitrate = DEFAULT_OPTIONS.audioBitrate,
    sampleRate = DEFAULT_OPTIONS.sampleRate,
    echoCancellation = DEFAULT_OPTIONS.echoCancellation,
    noiseSuppression = DEFAULT_OPTIONS.noiseSuppression,
    onChunkReady,
    onRecordingStart,
    onRecordingStop,
    onError,
    onAudioLevelUpdate,
  } = options;

  const [state, setState] = useState<BrowserRecordingState>(INITIAL_STATE);

  // Refs for managing recording resources
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const durationIntervalRef = useRef<number | null>(null);
  const chunkCounterRef = useRef(0);
  const startTimeRef = useRef<number>(0);
  const uploadedChunksRef = useRef(0);

  // Wake lock management
  const wakeLock = useWakeLock();

  /**
   * Update state helper
   */
  const updateState = useCallback((updates: Partial<BrowserRecordingState>) => {
    setState((prev) => {
      const newState = { ...prev, ...updates };
      return newState;
    });
  }, []);

  /**
   * Update audio level from analyser
   */
  const updateAudioLevel = useCallback(() => {
    if (!analyserRef.current) return;

    const analyser = analyserRef.current;
    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    analyser.getByteFrequencyData(dataArray);

    // Calculate average volume
    const sum = dataArray.reduce((a, b) => a + b, 0);
    const average = sum / dataArray.length;

    // Normalize to 0-1
    const normalized = average / 255;

    // Calculate decibels (approximate)
    const decibels = normalized > 0 ? 20 * Math.log10(normalized) : -100;

    // Get peak (max value in the array)
    const peak = Math.max(...dataArray) / 255;

    const audioLevel: AudioLevelData = {
      decibels: Math.max(-100, decibels),
      normalized,
      peak,
      level: classifyAudioLevel(normalized),
    };

    updateState({ audioLevel });
    onAudioLevelUpdate?.(audioLevel);

    // Continue animation loop
    animationFrameRef.current = requestAnimationFrame(updateAudioLevel);
  }, [onAudioLevelUpdate, updateState]);

  /**
   * Set up audio analysis for decibel meter
   */
  const setupAudioAnalysis = useCallback(
    (stream: MediaStream) => {
      try {
        // Create audio context
        const AudioContextClass =
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          window.AudioContext || (window as any).webkitAudioContext;
        const audioContext = new AudioContextClass();
        audioContextRef.current = audioContext;

        // Create analyser node
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.8;
        analyserRef.current = analyser;

        // Connect stream to analyser
        const source = audioContext.createMediaStreamSource(stream);
        source.connect(analyser);

        // Start audio level monitoring
        updateAudioLevel();
      } catch (err) {
        console.error("Failed to set up audio analysis:", err);
      }
    },
    [updateAudioLevel]
  );

  /**
   * Clean up audio analysis resources
   */
  const cleanupAudioAnalysis = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {
        // Ignore close errors
      });
      audioContextRef.current = null;
    }

    analyserRef.current = null;
  }, []);

  /**
   * Request microphone permission and set up stream
   */
  const requestPermission = useCallback(async (): Promise<MediaStream> => {
    updateState({ status: "requesting_permission" });

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation,
          noiseSuppression,
          sampleRate,
        },
      });

      mediaStreamRef.current = stream;
      setupAudioAnalysis(stream);

      return stream;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to access microphone";

      let userFriendlyMessage = errorMessage;
      if (
        errorMessage.includes("Permission denied") ||
        errorMessage.includes("NotAllowedError")
      ) {
        userFriendlyMessage =
          "Microphone access denied. Please allow microphone access in your browser settings.";
      } else if (
        errorMessage.includes("NotFoundError") ||
        errorMessage.includes("DevicesNotFoundError")
      ) {
        userFriendlyMessage =
          "No microphone found. Please connect a microphone and try again.";
      }

      updateState({
        status: "error",
        error: userFriendlyMessage,
      });
      onError?.(userFriendlyMessage);
      throw new Error(userFriendlyMessage);
    }
  }, [
    echoCancellation,
    noiseSuppression,
    sampleRate,
    setupAudioAnalysis,
    updateState,
    onError,
  ]);

  /**
   * Start recording
   */
  const startRecording = useCallback(async (): Promise<string> => {
    // Check browser compatibility
    const compatibility = checkBrowserCompatibility();
    if (!compatibility.mediaRecorder) {
      const error = "MediaRecorder API not supported in this browser";
      updateState({ status: "error", error });
      onError?.(error);
      throw new Error(error);
    }

    const mimeType = compatibility.recommendedMimeType;
    if (!mimeType) {
      const error = "No supported audio format found in this browser";
      updateState({ status: "error", error });
      onError?.(error);
      throw new Error(error);
    }

    try {
      // Request microphone access if not already granted
      let stream = mediaStreamRef.current;
      if (!stream) {
        stream = await requestPermission();
      }

      // Generate session ID
      const sessionId = generateSessionId();
      chunkCounterRef.current = 0;
      uploadedChunksRef.current = 0;
      startTimeRef.current = Date.now();

      // Request wake lock
      const wakeLockActive = await wakeLock.request();

      // Create MediaRecorder
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType,
        audioBitsPerSecond: audioBitrate,
      });
      mediaRecorderRef.current = mediaRecorder;

      // Handle data available (chunk ready)
      mediaRecorder.ondataavailable = async (event) => {
        if (event.data.size > 0) {
          const chunkNumber = ++chunkCounterRef.current;

          updateState({ chunksRecorded: chunkNumber });

          // Call chunk ready callback
          if (onChunkReady) {
            try {
              await onChunkReady(event.data, chunkNumber);
              uploadedChunksRef.current++;
              updateState({ chunksUploaded: uploadedChunksRef.current });
            } catch (err) {
              console.error(`Failed to upload chunk ${chunkNumber}:`, err);
              // Don't stop recording on upload failure - we'll retry later
            }
          }
        }
      };

      // Handle recording stop
      mediaRecorder.onstop = () => {
        onRecordingStop?.(sessionId, chunkCounterRef.current);
      };

      // Handle recording error
      mediaRecorder.onerror = (event) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const errorMessage = (event as any).error?.message || "Recording error";
        updateState({
          status: "error",
          error: errorMessage,
        });
        onError?.(errorMessage);
      };

      // Start recording with time slicing for chunks
      mediaRecorder.start(chunkDurationMs);

      // Start duration timer
      durationIntervalRef.current = window.setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
        updateState({ duration: elapsed });
      }, 1000);

      updateState({
        status: "recording",
        sessionId,
        wakeLockActive,
        mimeType,
        duration: 0,
        chunksRecorded: 0,
        chunksUploaded: 0,
        error: null,
      });

      onRecordingStart?.(sessionId);

      return sessionId;
    } catch (err) {
      if (state.status !== "error") {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to start recording";
        updateState({
          status: "error",
          error: errorMessage,
        });
        onError?.(errorMessage);
      }
      throw err;
    }
  }, [
    audioBitrate,
    chunkDurationMs,
    requestPermission,
    wakeLock,
    onChunkReady,
    onRecordingStart,
    onRecordingStop,
    onError,
    updateState,
    state.status,
  ]);

  /**
   * Stop recording
   */
  const stopRecording = useCallback(async (): Promise<{
    sessionId: string;
    totalChunks: number;
    duration: number;
  }> => {
    updateState({ status: "stopping" });

    // Stop duration timer
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }

    // Calculate final duration
    const duration = Math.floor((Date.now() - startTimeRef.current) / 1000);

    // Stop MediaRecorder and wait for final chunk to be processed
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      // Create a promise that resolves when the final chunk is uploaded
      const finalChunkPromise = new Promise<void>((resolve) => {
        const mediaRecorder = mediaRecorderRef.current!;

        // Replace with a handler that waits for upload
        mediaRecorder.ondataavailable = async (event) => {
          if (event.data.size > 0) {
            const chunkNumber = ++chunkCounterRef.current;

            updateState({ chunksRecorded: chunkNumber });

            // Call chunk ready callback and wait for upload
            if (onChunkReady) {
              try {
                await onChunkReady(event.data, chunkNumber);
                uploadedChunksRef.current++;
                updateState({ chunksUploaded: uploadedChunksRef.current });
              } catch (err) {
                console.error(
                  `Failed to upload final chunk ${chunkNumber}:`,
                  err
                );
              }
            }
          }
          resolve();
        };

        // Stop the recorder (this triggers ondataavailable with remaining data)
        mediaRecorder.stop();
      });

      // Wait for the final chunk to be uploaded
      await finalChunkPromise;
    }

    // Release wake lock
    await wakeLock.release();

    const result = {
      sessionId: state.sessionId || "",
      totalChunks: chunkCounterRef.current,
      duration,
    };

    updateState({
      status: "completed",
      duration,
      wakeLockActive: false,
    });

    return result;
  }, [state.sessionId, wakeLock, updateState, onChunkReady]);

  /**
   * Pause recording
   */
  const pauseRecording = useCallback(() => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "recording"
    ) {
      mediaRecorderRef.current.pause();
      updateState({ status: "paused" });
    }
  }, [updateState]);

  /**
   * Resume recording
   */
  const resumeRecording = useCallback(() => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "paused"
    ) {
      mediaRecorderRef.current.resume();
      updateState({ status: "recording" });
    }
  }, [updateState]);

  /**
   * Cancel recording and clean up
   */
  const cancelRecording = useCallback(async () => {
    // Stop duration timer
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }

    // Stop MediaRecorder
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
    }

    // Stop media stream
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }

    // Clean up audio analysis
    cleanupAudioAnalysis();

    // Release wake lock
    await wakeLock.release();

    // Reset state
    updateState(INITIAL_STATE);
    chunkCounterRef.current = 0;
    uploadedChunksRef.current = 0;
  }, [cleanupAudioAnalysis, wakeLock, updateState]);

  /**
   * Reset to initial state (after successful completion)
   */
  const reset = useCallback(() => {
    // Stop media stream if still active
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }

    // Clean up audio analysis
    cleanupAudioAnalysis();

    // Reset state
    updateState(INITIAL_STATE);
    chunkCounterRef.current = 0;
    uploadedChunksRef.current = 0;
  }, [cleanupAudioAnalysis, updateState]);

  /**
   * Check if microphone permission is granted
   */
  const checkPermission = useCallback(async (): Promise<boolean> => {
    try {
      const result = await navigator.permissions.query({
        name: "microphone" as PermissionName,
      });
      return result.state === "granted";
    } catch {
      // Some browsers don't support permission query for microphone
      return false;
    }
  }, []);

  /**
   * Clean up on unmount
   */
  useEffect(() => {
    return () => {
      // Stop duration timer
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }

      // Stop MediaRecorder
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state !== "inactive"
      ) {
        mediaRecorderRef.current.stop();
      }

      // Stop media stream
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      }

      // Clean up audio analysis
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {
          // Ignore close errors
        });
      }
    };
  }, []);

  return {
    state,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    cancelRecording,
    reset,
    requestPermission,
    checkPermission,
    checkBrowserCompatibility,
  };
}
